import { API_URL } from '@/lib/utils';
import { isNetworkError, trackedFetch } from '@/services/serverStatus';


export class HttpError extends Error {
  status: number;
  constructor(status: number, message?: string) {
    super(message ?? `HTTP ${status}`);
    this.name = 'HttpError';
    this.status = status;
  }
}


export function isForbiddenError(err: unknown): boolean {
  if (err instanceof HttpError) return err.status === 403;
  return err instanceof Error && /\b403\b/.test(err.message);
}

let isRefreshing = false;
type QueueEntry = { resolve: (token: string) => void; reject: (err: unknown) => void };
const refreshQueue: QueueEntry[] = [];

function drainQueue(newToken: string) {
  refreshQueue.splice(0).forEach(({ resolve }) => resolve(newToken));
}

function rejectQueue(err: unknown) {
  refreshQueue.splice(0).forEach(({ reject }) => reject(err));
}


function forceLogout() {
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('user');
  window.dispatchEvent(new Event('auth:logout'));
}


async function doRefresh(): Promise<string> {
  const refreshToken = localStorage.getItem('refresh_token');
  if (!refreshToken) throw new Error('No refresh token stored');

  const res = await trackedFetch(`${API_URL}/user/refresh`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refresh_token: refreshToken }),
  });

  if (!res.ok) throw new Error(`Refresh failed: ${res.status}`);

  const { access_token } = await res.json();
  localStorage.setItem('access_token', access_token);
  return access_token;
}


export async function apiFetch(url: string, options: RequestInit = {}): Promise<Response> {
  const token = localStorage.getItem('access_token');

  const makeHeaders = (t: string | null): HeadersInit => ({
    'Content-Type': 'application/json',
    ...(t ? { Authorization: `Bearer ${t}` } : {}),
    ...(options.headers as Record<string, string> | undefined),
  });

  const response = await trackedFetch(url, { ...options, headers: makeHeaders(token) });

  if (response.status !== 401) return response;


  if (isRefreshing) {
    return new Promise<Response>((resolve, reject) => {
      refreshQueue.push({
        resolve: (newToken) => {
          trackedFetch(url, { ...options, headers: makeHeaders(newToken) }).then(resolve).catch(reject);
        },
        reject,
      });
    });
  }

  isRefreshing = true;
  try {
    const newToken = await doRefresh();
    drainQueue(newToken);
    isRefreshing = false;
    return trackedFetch(url, { ...options, headers: makeHeaders(newToken) });
  } catch (err) {
    rejectQueue(err);
    isRefreshing = false;
    if (!isNetworkError(err)) forceLogout();
    return response;
  }
}

export async function apiFetchJson<T>(url: string, options: RequestInit = {}): Promise<T> {
  const res = await apiFetch(url, options);
  if (!res.ok) throw new HttpError(res.status, `HTTP ${res.status}: ${await res.text()}`);
  return res.json() as Promise<T>;
}

export async function apiDownloadFile(url: string, filename: string, options: RequestInit = {}): Promise<void> {
  const res = await apiFetch(url, { ...options, method: options.method ?? "GET" });
  if (!res.ok) throw new Error(`HTTP ${res.status}: ${await res.text()}`);

  const blob = await res.blob();
  const objectUrl = window.URL.createObjectURL(blob);
  const downloadedFilename = getDownloadFilename(res.headers.get("Content-Disposition")) ?? filename;

  try {
    const link = document.createElement("a");
    link.href = objectUrl;
    link.download = downloadedFilename;
    document.body.appendChild(link);
    link.click();
    link.remove();
  } finally {
    window.URL.revokeObjectURL(objectUrl);
  }
}

function getDownloadFilename(
  contentDisposition: string | null,
): string | null {
  if (!contentDisposition) return null;

  const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);

  if (utf8Match?.[1]) {
    try {
      return decodeURIComponent(utf8Match[1].trim());
    } catch {
      return utf8Match[1].trim();
    }
  }

  const regularMatch = contentDisposition.match(
    /filename\s*=\s*"([^"]+)"|filename\s*=\s*([^;]+)/i,
  );

  const filename = regularMatch?.[1] ?? regularMatch?.[2];

  return filename?.trim() ?? null;
}